/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalCredentialsDeleteGcpIam } from "../funcs/externalCredentialsDeleteGcpIam.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteGcpIamCredentialRequest,
  DeleteGcpIamCredentialSecurity,
} from "../models/operations/deletegcpiamcredential.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteGcpIamCredentialMutationVariables = {
  request: DeleteGcpIamCredentialRequest;
  security?: DeleteGcpIamCredentialSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteGcpIamCredentialMutationData = void;

export type DeleteGcpIamCredentialMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteGcpIamCredential externalCredentials
 *
 * @remarks
 * Soft-delete a GCP IAM external credential by ID. Requires org:admin. Refused with a conflict while any live external key still names the credential, since deleting it would leave those keys unable to reach the key material they sign with.
 */
export function useDeleteGcpIamCredentialMutation(
  options?: MutationHookOptions<
    DeleteGcpIamCredentialMutationData,
    DeleteGcpIamCredentialMutationError,
    DeleteGcpIamCredentialMutationVariables
  >,
): UseMutationResult<
  DeleteGcpIamCredentialMutationData,
  DeleteGcpIamCredentialMutationError,
  DeleteGcpIamCredentialMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteGcpIamCredentialMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteGcpIamCredential(): MutationKey {
  return ["@gram/client", "externalCredentials", "deleteGcpIam"];
}

export function buildDeleteGcpIamCredentialMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteGcpIamCredentialMutationVariables,
  ) => Promise<DeleteGcpIamCredentialMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteGcpIamCredential(),
    mutationFn: function deleteGcpIamCredentialMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteGcpIamCredentialMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalCredentialsDeleteGcpIam(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
